import React, { useEffect, useMemo, useState } from 'react';
import AulaMensajesPanel from './AulaMensajesPanel';
import AulaArchivosPanel from './AulaArchivosPanel';
import AulaAsistentesPanel from './AulaAsistentesPanel';
import AulaLinksPanel from './AulaLinksPanel';
import './AulaRecursosModal.css';

const getBoolValue = (source, camelKey, pascalKey, fallback = true) => {
  if (!source) return fallback;
  if (typeof source[camelKey] === 'boolean') return source[camelKey];
  if (typeof source[pascalKey] === 'boolean') return source[pascalKey];
  return fallback;
};

const buildResourceVisibility = (source) => ({
  mostrarArchivos: getBoolValue(source, 'mostrarArchivos', 'MostrarArchivos'),
  mostrarLinks: getBoolValue(source, 'mostrarLinks', 'MostrarLinks'),
  mostrarPizarra: getBoolValue(source, 'mostrarPizarra', 'MostrarPizarra'),
  mostrarChat: getBoolValue(source, 'mostrarChat', 'MostrarChat'),
  mostrarVideollamada: getBoolValue(source, 'mostrarVideollamada', 'MostrarVideollamada'),
  mostrarForo: getBoolValue(source, 'mostrarForo', 'MostrarForo'),
  mostrarAsistentesIA: getBoolValue(source, 'mostrarAsistentesIA', 'MostrarAsistentesIA')
});

const mergeAulaVisibility = (aula, visibility) => ({
  ...aula,
  ...visibility,
  MostrarArchivos: visibility.mostrarArchivos,
  MostrarLinks: visibility.mostrarLinks,
  MostrarPizarra: visibility.mostrarPizarra,
  MostrarChat: visibility.mostrarChat,
  MostrarVideollamada: visibility.mostrarVideollamada,
  MostrarForo: visibility.mostrarForo,
  MostrarAsistentesIA: visibility.mostrarAsistentesIA
});

const RESOURCE_VISIBILITY_CONFIG = [
  { key: 'mostrarArchivos', label: 'Archivos', icon: 'fa-folder-open' },
  { key: 'mostrarLinks', label: 'Links', icon: 'fa-link' },
  { key: 'mostrarPizarra', label: 'Pizarra', icon: 'fa-chalkboard' },
  { key: 'mostrarChat', label: 'Chat', icon: 'fa-comments' },
  { key: 'mostrarVideollamada', label: 'Video llamada', icon: 'fa-video' },
  { key: 'mostrarForo', label: 'Foro', icon: 'fa-comments' },
  { key: 'mostrarAsistentesIA', label: 'Asistentes IA', icon: 'fa-robot' }
];

const AulaRecursosModal = ({
  open,
  aula,
  onClose,
  hasMensajes = false,
  hasArchivos = false,
  hasAsistentes = false,
  hasLinks = false,
  canUploadFiles = true,
  labels = {},
  mensajesService,
  archivosService,
  linksService,
  asistentesService,
  resourceVisibilityService,
  globalAvailability = {},
  onAulaUpdated,
  showAlumnoFilter = true
}) => {
  const aulaId = aula?.id || aula?.Id || null;
  const aulaNombre = aula?.nombre || aula?.Nombre || 'Aula';
  const resourceLabel = labels.resource || 'aula';
  const resourceLabelTitle = labels.resourceTitle || 'aula';

  const tabs = useMemo(() => {
    const nextTabs = [];

    if (hasMensajes) {
      nextTabs.push({
        id: 'mensajes',
        label: 'Mensajes',
        description: 'Foro y publicaciones',
        icon: 'fa-comments'
      });
    }

    if (hasArchivos) {
      nextTabs.push({
        id: 'archivos',
        label: 'Archivos',
        description: 'Materiales y descargas',
        icon: 'fa-folder-open'
      });
    }

    if (hasAsistentes) {
      nextTabs.push({
        id: 'asistentes',
        label: 'Asistentes IA',
        description: 'Asistentes IA vinculados',
        icon: 'fa-robot'
      });
    }

    if (hasLinks) {
      nextTabs.push({
        id: 'links',
        label: 'Links',
        description: 'Recursos externos del aula',
        icon: 'fa-link'
      });
    }

    return nextTabs;
  }, [hasArchivos, hasAsistentes, hasLinks, hasMensajes]);

  const [activeTab, setActiveTab] = useState('');
  const [visibility, setVisibility] = useState(() => buildResourceVisibility(aula));
  const [visibilitySavingKey, setVisibilitySavingKey] = useState('');
  const [visibilityError, setVisibilityError] = useState('');

  const availability = useMemo(() => ({
    mostrarArchivos: hasArchivos,
    mostrarLinks: hasLinks,
    mostrarPizarra: false,
    mostrarChat: false,
    mostrarVideollamada: false,
    mostrarForo: hasMensajes,
    mostrarAsistentesIA: hasAsistentes,
    ...globalAvailability
  }), [globalAvailability, hasArchivos, hasAsistentes, hasLinks, hasMensajes]);

  useEffect(() => {
    if (!open) {
      setActiveTab('');
      return;
    }

    if (tabs.length === 0) {
      setActiveTab('');
      return;
    }

    if (!tabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(tabs[0].id);
    }
  }, [activeTab, open, tabs]);

  useEffect(() => {
    if (open) {
      setVisibility(buildResourceVisibility(aula));
      setVisibilityError('');
      setVisibilitySavingKey('');
    }
  }, [aula, open]);

  const handleVisibilityToggle = async (key) => {
    if (!aulaId || !resourceVisibilityService || visibilitySavingKey) return;

    const previous = visibility;
    const nextVisibility = {
      ...visibility,
      [key]: !visibility[key]
    };

    setVisibility(nextVisibility);
    setVisibilitySavingKey(key);
    setVisibilityError('');

    try {
      const response = await resourceVisibilityService(aulaId, nextVisibility);
      const responseVisibility = buildResourceVisibility(response?.recursos || response?.Recursos || nextVisibility);
      setVisibility(responseVisibility);
      onAulaUpdated?.(mergeAulaVisibility(aula, responseVisibility));
    } catch (error) {
      setVisibility(previous);
      setVisibilityError(error?.message || 'No pudimos actualizar la visibilidad.');
    } finally {
      setVisibilitySavingKey('');
    }
  };

  if (!open) return null;

  return (
    <div className="aula-resources-overlay" onClick={onClose}>
      <div className="aula-resources-modal" onClick={(event) => event.stopPropagation()}>
        <div className="aula-resources-header">
          <div>
            <p className="aula-resources-eyebrow">Recursos del {resourceLabel}</p>
            <h3>{aulaNombre}</h3>
            <p className="aula-resources-subtitle">
              Centralizá mensajes, archivos, asistentes IA y links desde un único lugar.
            </p>
          </div>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cerrar
          </button>
        </div>

        <section className="aula-resources-visibility">
          <div className="aula-resources-visibility-head">
            <h4>Visibilidad en el aula</h4>
            {visibilityError && <span className="aula-resources-visibility-error">{visibilityError}</span>}
          </div>
          <div className="aula-resources-visibility-grid">
            {RESOURCE_VISIBILITY_CONFIG.map((item) => {
              const isAvailable = availability[item.key] !== false;
              const isVisible = Boolean(visibility[item.key]);
              const isSaving = visibilitySavingKey === item.key;

              return (
                <label
                  key={item.key}
                  className={`aula-resource-switch${!isAvailable ? ' disabled' : ''}${isVisible ? ' visible' : ''}`}
                >
                  <span className="aula-resource-switch-icon">
                    <i className={`fas ${item.icon}`} />
                  </span>
                  <span className="aula-resource-switch-copy">
                    <strong>{item.label}</strong>
                    <small>{!isAvailable ? 'No habilitado' : isVisible ? 'Visible' : 'Oculto'}</small>
                  </span>
                  <input
                    type="checkbox"
                    checked={isVisible && isAvailable}
                    disabled={!isAvailable || !resourceVisibilityService || Boolean(visibilitySavingKey)}
                    onChange={() => handleVisibilityToggle(item.key)}
                  />
                  <span className="aula-resource-switch-track">
                    <span className="aula-resource-switch-thumb">
                      {isSaving && <i className="fas fa-spinner fa-spin" />}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        </section>

        {tabs.length === 0 ? (
          <div className="aula-resources-empty">
            <i className="fas fa-box-open" />
            <p>No hay recursos habilitados para esta {resourceLabelTitle}.</p>
          </div>
        ) : (
          <div className="aula-resources-layout">
            <aside className="aula-resources-nav">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`aula-resources-tab${activeTab === tab.id ? ' active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <span className="aula-resources-tab-icon">
                    <i className={`fas ${tab.icon}`} />
                  </span>
                  <span className="aula-resources-tab-copy">
                    <strong>{tab.label}</strong>
                    <small>{tab.description}</small>
                  </span>
                </button>
              ))}
            </aside>

            <section className="aula-resources-content">
              {activeTab === 'mensajes' && (
                <AulaMensajesPanel
                  aulaId={aulaId}
                  embedded
                  service={mensajesService}
                  showAlumnoFilter={showAlumnoFilter}
                />
              )}
              {activeTab === 'archivos' && (
                <AulaArchivosPanel
                  aulaId={aulaId}
                  aulaNombre={aulaNombre}
                  canUpload={canUploadFiles}
                  service={archivosService}
                  resourceLabel={resourceLabel}
                />
              )}
              {activeTab === 'asistentes' && (
                <AulaAsistentesPanel aulaId={aulaId} service={asistentesService} />
              )}
              {activeTab === 'links' && (
                <AulaLinksPanel aulaId={aulaId} service={linksService} />
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default AulaRecursosModal;
